import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import type { Profile } from '@/lib/supabase/client';
import { toast } from 'react-hot-toast';
import { Database } from '@/lib/database.types';

interface SocialLinks {
  id?: string;
  user_id?: string;
  twitter_url: string | null;
  linkedin_url: string | null;
  github_url: string | null;
  website_url: string | null;
  instagram_url: string | null;
  facebook_url: string | null;
  created_at?: string;
  updated_at?: string | null;
}

interface UserPreferences {
  id?: string;
  user_id?: string;
  email_notifications: boolean;
  created_at?: string;
  updated_at?: string | null;
}

interface User {
  id: string;
  email?: string;
  created_at: string;
}

export interface ProfileUpdateData {
  username?: string;
  display_name?: string;
  bio?: string;
}

export interface AuthState {
  // State
  profile: Profile | null;
  isLoading: boolean;
  error: string | null;
  user: User | null;
  socialLinks: SocialLinks | null;
  userPreferences: UserPreferences | null;
  
  // Actions
  fetchProfile: () => Promise<Profile | null>;
  upgradeToCreator: () => Promise<void>;
  clearError: () => void;
  updateAvatar: (avatarUrl: string) => Promise<void>;
  updateProfile: (profile: Partial<Profile>) => Promise<void>;
  
  // Social links methods
  fetchSocialLinks: () => Promise<SocialLinks | null>;
  updateSocialLinks: (links: Partial<SocialLinks>) => Promise<void>;
  
  // User preferences methods
  fetchUserPreferences: () => Promise<UserPreferences | null>;
  updateUserPreferences: (preferences: Partial<UserPreferences>) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  profile: null,
  isLoading: false,
  error: null,
  user: null,
  socialLinks: null,
  userPreferences: null,
  
  fetchProfile: async () => {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        // Check if we're in an auth flow
        const isAuthFlow = 
          typeof window !== 'undefined' && (
            window.location.pathname.includes('/auth/') ||
            window.location.pathname === '/' ||
            window.location.pathname.includes('/sign-up')
          );
        
        if (isAuthFlow) {
          // Just debug log during auth flow
          console.debug('Auth flow info: No user available for profile fetch (expected)');
        } else {
          console.error('Error getting user for profile:', authError);
        }
        
        set({ profile: null, user: null });
        return null;
      }
      
      if (!user) {
        set({ profile: null, user: null });
        return null;
      }
      
      set({ isLoading: true, error: null, user: user });
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
          
        if (error) {
          // Check if this is during signup/auth flow
          const isAuthFlow = typeof window !== 'undefined' && window.location.pathname.includes('/auth/');
          
          // If we're in an auth flow, profile might not exist yet
          if (isAuthFlow) {
            console.debug('Auth flow info: No profile yet (expected during signup)');
            set({ isLoading: false });
            return null;
          }
          
          throw error;
        }
        
        set({ profile: data as Profile, isLoading: false });
        
        // Also fetch social links and preferences
        await get().fetchSocialLinks();
        await get().fetchUserPreferences();
        
        return data as Profile;
      } catch (error: any) {
        // Check if we're in an auth flow before setting error
        const isAuthFlow = 
          typeof window !== 'undefined' && (
            window.location.pathname.includes('/auth/') ||
            window.location.pathname === '/' ||
            window.location.pathname.includes('/sign-up')
          );
          
        if (!isAuthFlow) {
          set({ error: error.message, isLoading: false });
        } else {
          set({ isLoading: false });
          console.debug('Auth flow info: Error fetching profile (expected during auth flow)');
        }
        
        return null;
      }
    } catch (error: any) {
      // Check if we're in an auth flow
      const isAuthFlow = 
        typeof window !== 'undefined' && (
          window.location.pathname.includes('/auth/') ||
          window.location.pathname === '/' ||
          window.location.pathname.includes('/sign-up')
        );
        
      if (!isAuthFlow) {
        console.error('Error in fetchProfile:', error);
        set({ error: error.message, isLoading: false });
      } else {
        set({ isLoading: false });
        console.debug('Auth flow info: Error in fetchProfile (expected during auth flow)');
      }
      
      return null;
    }
  },
  
  upgradeToCreator: async () => {
    const { profile } = get();
    
    if (!profile) {
      set({ error: 'You must be logged in to become a creator' });
      return;
    }
    
    set({ isLoading: true, error: null });
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: 'creator' })
        .eq('id', profile.id);
        
      if (error) throw error;
      
      // Refresh the profile to get updated data
      await get().fetchProfile();
      
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },
  
  updateAvatar: async (avatarUrl: string) => {
    const { profile } = get();
    
    if (!profile) {
      set({ error: 'You must be logged in to update your avatar' });
      return;
    }
    
    set({ isLoading: true, error: null });
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: avatarUrl })
        .eq('id', profile.id);
        
      if (error) throw error;
      
      // Refresh the profile to get updated data
      await get().fetchProfile();
      
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },
  
  updateProfile: async (profile: Partial<Profile>) => {
    const { profile: existingProfile } = get();
    
    if (!existingProfile) {
      set({ error: 'You must be logged in to update your profile' });
      return;
    }
    
    set({ isLoading: true, error: null });
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update(profile)
        .eq('id', existingProfile.id);
        
      if (error) throw error;
      
      // Refresh the profile to get updated data
      await get().fetchProfile();
      
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error; // Re-throw the error to be caught by the form
    }
  },
  
  clearError: () => set({ error: null }),
  
  fetchSocialLinks: async () => {
    const { user } = get();
    const userId = user?.id;
    
    if (!userId) {
      console.log('Cannot fetch social links: No user ID available');
      return null;
    }
    
    try {
      console.log(`Fetching social links for user ID: ${userId}`);
      
      // Skip the RPC approach temporarily due to 406 errors
      // Use direct query instead
      const { data, error } = await supabase
        .from('user_social_links')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
        
      if (error) {
        // If it's a "not found" error (404), this is normal for first-time users
        if (error.code === 'PGRST116') {
          console.log('No social links found for user - this is normal for first use');
          return null;
        }
        
        console.error('Error fetching social links:', error);
        return null;
      }
      
      // Set in state and return
      set({ socialLinks: data });
      return data;
    } catch (error: any) {
      console.error('Unexpected error fetching social links:', error);
      return null;
    }
  },
  
  updateSocialLinks: async (links: Partial<SocialLinks>) => {
    const { user } = get();
    const userId = user?.id;
    
    if (!userId) {
      console.error('Cannot update social links: No user ID available');
      toast.error('Authentication error. Please try logging in again.');
      return;
    }
    
    try {
      console.log(`Updating social links for user ID: ${userId}`);
      
      const { data, error } = await supabase
        .rpc('upsert_social_links', {
          p_user_id: userId,
          p_twitter_url: links.twitter_url ?? null,
          p_linkedin_url: links.linkedin_url ?? null,
          p_github_url: links.github_url ?? null,
          p_website_url: links.website_url ?? null,
          p_instagram_url: links.instagram_url ?? null,
          p_facebook_url: links.facebook_url ?? null
        });
        
      if (error) {
        console.error('Error updating social links:', error);
        toast.error('Failed to update social links');
        throw error;
      }
      
      console.log('Social links updated successfully:', data);
      
      // Refresh social links in profile
      await get().fetchSocialLinks();
      toast.success('Social links updated successfully');
    } catch (error) {
      console.error('Unexpected error updating social links:', error);
      toast.error('An unexpected error occurred while updating social links');
    }
  },
  
  fetchUserPreferences: async () => {
    const { user } = get();
    const userId = user?.id;
    
    if (!userId) {
      console.log('Cannot fetch user preferences: No user ID available');
      return null;
    }
    
    try {
      console.log(`Fetching preferences for user ID: ${userId}`);
      
      // Skip the RPC approach temporarily due to 406 errors
      // Use direct query instead
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
        
      if (error) {
        // If it's a "not found" error (404), this is normal for first-time users
        if (error.code === 'PGRST116') {
          console.log('No preferences found for user - this is normal for first use');
          return null;
        }
        
        console.error('Error fetching user preferences:', error);
        return null;
      }
      
      // Set in state and return
      set({ userPreferences: data });
      return data;
    } catch (error: any) {
      console.error('Unexpected error fetching user preferences:', error);
      return null;
    }
  },
  
  updateUserPreferences: async (preferences: Partial<UserPreferences>) => {
    const { user } = get();
    const userId = user?.id;
    
    if (!userId) {
      console.error('Cannot update user preferences: No user ID available');
      toast.error('Authentication error. Please try logging in again.');
      return;
    }
    
    if (preferences.email_notifications === undefined) {
      console.error('Email notifications preference is required');
      toast.error('Email notifications preference is required');
      return;
    }
    
    try {
      console.log(`Updating preferences for user ID: ${userId}`);
      
      const { data, error } = await supabase
        .rpc('upsert_user_preferences', {
          p_user_id: userId,
          p_email_notifications: preferences.email_notifications
        });
        
      if (error) {
        console.error('Error updating user preferences:', error);
        toast.error('Failed to update preferences');
        throw error;
      }
      
      console.log('User preferences updated successfully:', data);
      
      // Refresh user preferences in profile
      await get().fetchUserPreferences();
      toast.success('Preferences updated successfully');
    } catch (error) {
      console.error('Unexpected error updating user preferences:', error);
      toast.error('An unexpected error occurred while updating preferences');
    }
  },
})); 